import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../utils/api';
import { panelStyle, modalSurface, surfaces } from '../styles/desktopStyles';

import careerStarLogo from '../assets/images/career-star-logo-white.png';
import astroaut from '../assets/images/main-page-astronaut.png';
import StarField from './StarField';

// Flyer-style building blocks (mirrors the top of /CUNY2026).
const panel = { ...panelStyle, padding: 28, marginBottom: 24, textAlign: 'left' };
const heading = { fontFamily: 'var(--cs-font)', fontWeight: 700, color: '#fff', margin: '0 0 12px 0' };
const body = { color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.6, margin: '0 0 12px 0' };

const Prob = ({ color, title, text }) => (
    <div style={{ borderLeft: `3px solid ${color}`, borderRadius: 8, padding: '12px 14px', background: 'rgba(255,255,255,0.05)' }}>
        <h4 style={{ fontFamily: 'var(--cs-font)', fontSize: 14, color, marginTop: 0, marginBottom: 6, fontWeight: 700 }}>{title}</h4>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', margin: 0, lineHeight: 1.55, fontWeight: 500 }}>{text}</p>
    </div>
);

function StartPage() {
    const navigate = useNavigate();

    const [showResendInvite, setShowResendInvite] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteStatus, setInviteStatus] = useState(null); // null | 'sending' | 'sent' | 'has_password' | 'error'
    const [inviteMessage, setInviteMessage] = useState('');

    const handleLogin = () => {
        navigate('/login');
    };

    const handleRequestInvite = async () => {
        if (!inviteEmail) return;
        setInviteStatus('sending');
        setInviteMessage('');
        try {
            const response = await axios.post(apiUrl('/api/auth/request-magic-link'), { email: inviteEmail });
            if (response.data.has_password) {
                setInviteStatus('has_password');
                setInviteMessage('This account already has a password. Use "Forgot password?" on the login page instead.');
            } else {
                setInviteStatus('sent');
                setInviteMessage(response.data.message || 'Check your email for a new invitation link.');
            }
        } catch (error) {
            setInviteStatus('error');
            setInviteMessage(error.response?.data?.error || 'Something went wrong. Please try again.');
        }
    };

    useEffect(() => {
        localStorage.removeItem('selectedPage');
    }, []);

    return (
        <div className='main-page' style={{ minHeight: '100vh', height: 'auto', justifyContent: 'flex-start' }}>
            <StarField />
            <div className='career-star-logo'>
                <img src={careerStarLogo} alt='Career Star Logo' />
            </div>

            <div style={{ width: '100%', maxWidth: 980, margin: '0 auto', padding: '120px 24px 60px', position: 'relative', zIndex: 1, boxSizing: 'border-box' }}>

                {/* Focused login — a sign-in card, not a marketing landing.
                    (Cut the triangle diagram + "Bridging the Classroom" pitch.)
                    The card has a real dark base rather than a translucent white veil: on the
                    starfield a 5-10% white wash leaves stars running through the text, which is
                    what made this page unreadable. */}
                {/* No card. The sign-in block sits directly on the page: a panel around three
                    elements was more box than content. */}
                <div style={{ maxWidth: 440, margin: '0 auto', textAlign: 'center' }}>
                    <img src={astroaut} alt='Astronaut' style={{ width: 140, maxWidth: '38vw', height: 'auto', marginBottom: 18 }} />
                    <h1 style={{ fontFamily: 'var(--cs-font)', fontWeight: 800, fontSize: 28, lineHeight: 1.2, letterSpacing: '-0.5px', color: '#fff', margin: '0 0 6px' }}>
                        Sign in to CareerStar
                    </h1>
                    <p style={{ fontFamily: 'var(--cs-font)', fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.65)', margin: '0 0 22px' }}>
                        Your launchpad for experiential learning.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
                        <div className='main-page-button' onClick={handleLogin} style={{ display: 'inline-flex' }}>
                            <p>Login</p>
                        </div>
                    </div>
                </div>

                {/* Request new invitation link — for users whose account was already set up. */}
                <div style={{ marginTop: 4, textAlign: 'center' }}>
                    {!showResendInvite ? (
                        <p
                            onClick={() => setShowResendInvite(true)}
                            style={{ color: '#c4b5fd', fontSize: 16, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, display: 'inline-block', margin: 0 }}
                        >
                            Need to set up your school account for the first time? Click here!
                        </p>
                    ) : (
                        <div style={{
                            ...panelStyle, ...modalSurface,
                            borderRadius: 12, padding: '16px 20px',
                            textAlign: 'left', maxWidth: 400, margin: '0 auto', marginBottom: 0,
                        }}>
                            <p style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 8, marginTop: 0 }}>
                                Request a new password link
                            </p>
                            <p style={{ color: '#a0a0b8', fontSize: 13, marginBottom: 12, marginTop: 0 }}>
                                Enter your email address associated with CareerStar
                            </p>
                            <input
                                type="email"
                                placeholder="your.email@school.edu"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    borderRadius: 8,
                                    border: '1px solid rgba(255,255,255,0.28)',
                                    background: surfaces.sunken,
                                    color: '#fff',
                                    fontSize: 14,
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    marginBottom: 12
                                }}
                            />
                            {inviteStatus === 'sent' && (
                                <p style={{ color: '#4ade80', fontSize: 13, margin: '0 0 10px 0' }}>{inviteMessage}</p>
                            )}
                            {inviteStatus === 'has_password' && (
                                <p style={{ color: '#fbbf24', fontSize: 13, margin: '0 0 10px 0' }}>{inviteMessage}</p>
                            )}
                            {inviteStatus === 'error' && (
                                <p style={{ color: '#ff6b6b', fontSize: 13, margin: '0 0 10px 0' }}>{inviteMessage}</p>
                            )}
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button
                                    onClick={handleRequestInvite}
                                    disabled={!inviteEmail || inviteStatus === 'sending'}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: 8,
                                        background: inviteStatus === 'sent' ? '#22c55e' : '#8B5CF6',
                                        color: '#fff',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        border: 'none',
                                        cursor: inviteStatus === 'sending' ? 'not-allowed' : 'pointer',
                                        opacity: (!inviteEmail || inviteStatus === 'sending') ? 0.6 : 1
                                    }}
                                >
                                    {inviteStatus === 'sending' ? 'Sending...' : inviteStatus === 'sent' ? 'Link Sent ✓' : 'Send Link'}
                                </button>
                                <button
                                    onClick={() => { setShowResendInvite(false); setInviteStatus(null); setInviteMessage(''); }}
                                    style={{
                                        padding: '10px 16px',
                                        borderRadius: 8,
                                        background: 'transparent',
                                        color: '#a0a0b8',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Make getting help obvious. */}
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, fontFamily: 'var(--cs-font)', marginTop: 18, textAlign: 'center' }}>
                    Need help getting in? Email <a href="mailto:hello@careerstar.co" style={{ color: '#a78bfa', fontWeight: 700, textDecoration: 'none' }}>hello@careerstar.co</a>
                </p>
            </div>
        </div>
    );
}

export default StartPage;
